import { utils } from "@coral-xyz/anchor";
import Big from "big.js";

import { AchievementType, HapiAchievementType, hapiAchievementsArray } from "@/interfaces/api";

import { formatTokenAmount } from "./amount";

export * from "./amount";
export * from "./string";

export const delay = async (time = 1000): Promise<void> => {
  return new Promise((res) => setTimeout(res, time));
};

export const getColorByScore = (value: number): string => {
  const colorPoints: {
    value: number;
    color: string;
  }[] = [
    { value: 0, color: "rgb(255,77,77)" },
    { value: 50, color: "rgb(253,191,30)" },
    { value: 100, color: "rgb(100,209,62)" },
  ];

  if (value <= colorPoints[0].value) {
    return colorPoints[0].color;
  }

  if (value >= colorPoints[colorPoints.length - 1].value) {
    return colorPoints[colorPoints.length - 1].color;
  }

  for (let i = 0; i < colorPoints.length - 1; i++) {
    if (value >= colorPoints[i].value && value <= colorPoints[i + 1].value) {
      const percent = (value - colorPoints[i].value) / (colorPoints[i + 1].value - colorPoints[i].value);
      const startColor = colorPoints[i].color.match(/\d+/g)!.map(Number);
      const endColor = colorPoints[i + 1].color.match(/\d+/g)!.map(Number);
      const interpolatedColor = startColor.map((channel, index) =>
        Math.round(channel + percent * (endColor[index] - channel)),
      );
      return `rgb(${interpolatedColor.join(",")})`;
    }
  }

  return "";
};

export const scaleHelpCircle = () => {
  document
    .getElementById("help-circle")
    ?.animate([{ transform: "scale(1)" }, { transform: "scale(1.4)" }, { transform: "scale(1)" }], {
      duration: 700,
      iterations: 5,
    });
};

export enum EEnvironment {
  STAGING = "staging",
  PRODUCTION = "production",
}

export const isDev = (): boolean => {
  const { hostname, origin } = window.location;
  const env = import.meta.env.VITE_ENVIRONMENT as EEnvironment;

  return (
    env === EEnvironment.STAGING ||
    hostname === "localhost" ||
    hostname.startsWith("127.") ||
    hostname.startsWith("192.") ||
    origin.includes("here-hapi-score.vercel.app") ||
    origin.includes("dsjqg9xj-5173.euw.devtunnels.ms")
  );
};

export const isHereWallet = (): boolean => {
  const { ancestorOrigins } = window.location;
  return (
    ancestorOrigins?.[0]?.includes("my.herewallet.app") ||
    ancestorOrigins?.[0]?.includes("tgapp.herewallet.app") ||
    ancestorOrigins?.[0]?.includes("tgapp-dev.herewallet.app") ||
    ancestorOrigins?.[0]?.includes("mobile0.herewallet.app") ||
    false
  );
};

export enum ApiVersion {
  v1 = "v1",
  v2 = "v2",
}

export const getApiVersion = (): ApiVersion => {
  return isHereWallet() ? ApiVersion.v1 : ApiVersion.v2;
};

export const apiVersion = getApiVersion();

export const platformVersion = import.meta.env.VITE_ENVIRONMENT as EEnvironment;

export const toArray = <T>(map: Record<string | number, T>): Array<T> => Object.values(map);

type KeyType = string | number | symbol;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toMap = <T extends Record<KeyType, any>, K extends keyof T>(
  array: Array<T>,
  keyName: K,
): Record<T[K], T> =>
  array.reduce(
    (acc, item) => ({
      ...acc,
      [item[keyName]]: item,
    }),
    {} as Record<T[K], T>,
  );

export function isNotNullOrUndefined<T extends object>(input: null | undefined | T): input is T {
  return input !== null && input !== undefined;
}

export const isHapiAchievementType = (id: AchievementType): id is HapiAchievementType => {
  return hapiAchievementsArray.includes(id as HapiAchievementType);
};

export function createObjectFromKeys<K extends string | number | symbol, V>(keys: K[], defaultValue: V): Record<K, V> {
  return Object.fromEntries(keys.map((key) => [key, defaultValue])) as Record<K, V>;
}

export const chunkArray = <T>(array: T[], size: number): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

export const calculateShouldReject = (priorityFeeEstimate?: number, feeAmount?: number, isRemint?: boolean) => {
  const feeLimitMultiplicator = 3;
  const computeUnits = isRemint ? 42000 : 80000;
  const priorityFee = Big(priorityFeeEstimate ?? 0);
  const basicFee = Big(formatTokenAmount(feeAmount ?? 0, 9));
  const estimate = Big(formatTokenAmount(priorityFee, 15)).mul(computeUnits); // priorityFeeEstimate in microlamports that has 9 decimals to lamports and 9 decimal to SOL
  if (Big(estimate).lte(basicFee.mul(feeLimitMultiplicator))) return false;

  return true;
};

export function padBuffer(buffer: Buffer | Uint8Array, targetSize: number) {
  if (!(buffer instanceof Buffer)) {
    buffer = Buffer.from(buffer);
  }

  if (buffer.byteLength > targetSize) {
    throw new RangeError(`Buffer is larger than target size: ${targetSize}`);
  }

  return Buffer.concat([buffer, Buffer.alloc(targetSize - buffer.byteLength)], targetSize);
}

export function bufferFromString(str: string, bufferSize?: number) {
  const utf = utils.bytes.utf8.encode(str);

  if (!bufferSize || utf.byteLength === bufferSize) {
    return Buffer.from(utf);
  }

  if (bufferSize && utf.byteLength > bufferSize) {
    throw RangeError("Buffer size too small to fit the string");
  }

  return padBuffer(utf, bufferSize);
}
